import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { webSessionHeaders } from '../auth/models/web-session.util';
import { base64UrlToBuffer, bufferToBase64Url } from '../auth/models/passkey.util';

/**
 * JS-1396 follow-up: thrown by PasskeyService.enrol() only for the
 * createCredential() step - the one step that runs entirely on-device and
 * sends no request. Callers that need to tell "the ceremony failed locally"
 * (token/session still good, safe to let the user retry) apart from "an
 * HTTP call failed" (registerOptions/registerVerify - token or session may
 * be spent) should check `instanceof PasskeyLocalCeremonyError`; anything
 * else thrown out of enrol() came from one of the two HTTP calls.
 */
export class PasskeyLocalCeremonyError extends Error {
  constructor(public readonly cause: any) {
    super('Passkey ceremony failed locally');
  }
}

@Injectable()
export class PasskeyService {
  // JS-1396: the root cause is src/app/_helpers/error.interceptor.ts's
  // catchError (line 108-109):
  //   const error = err.error?.error?.message || err.error?.message || err.statusText;
  //   return throwError(error);
  // By design, on a SINGLE pass through this interceptor, every HTTP error
  // is rethrown as a plain string (the message), never as the original
  // HttpErrorResponse. That already erases `.status` for every subscriber
  // downstream - confirmed live while building loginRecovery()'s caller,
  // which needs a real HTTP status to tell a 403 (wrong code, retry
  // allowed) from a 429 (attempt budget exhausted, bounce to /login) apart.
  //
  // Separately, app.module.ts and auth.module.ts each also register their
  // own copy of JwtInterceptor/ErrorInterceptor as HTTP_INTERCEPTORS
  // providers (a pre-existing, app-wide pattern - every lazy "page" module
  // under src/app/cms does the same), and because AuthModule is imported
  // eagerly into the same root injector as AppModule, requests from here
  // run the chain twice. That duplication is real and worth its own fix,
  // but it only changes WHICH flavour of nothing a subscriber receives (the
  // second pass re-runs the same extraction on the first pass's already-
  // stringified error and gets `undefined` instead of a string) - it is
  // NOT the cause. De-duplicating the registrations alone would still leave
  // every subscriber downstream of this interceptor with a string instead
  // of an HttpErrorResponse, so it would NOT remove the need for this
  // bypass. Do not revert `rawHttp` on the strength of that fix alone;
  // `error.status === 429` would go straight back to being dead code on
  // this screen's counted, non-renewable attempt budget.
  //
  // Fixing error.interceptor.ts itself (rethrowing the original
  // HttpErrorResponse, or at least preserving `.status`) is the real fix,
  // but changes behaviour for every caller in the app and is well outside
  // this ticket's scope. Instead, this one unauthenticated call
  // (login-recovery: JwtInterceptor is a no-op here anyway, since there is
  // no currentUser/api_token yet) is routed through a plain HttpClient
  // built on HttpBackend directly, which skips the registered interceptor
  // chain entirely and returns the genuine HttpErrorResponse.
  private rawHttp: HttpClient;

  constructor(private http: HttpClient, httpBackend: HttpBackend) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  // JS-1396 [A]: the optional enrolmentToken is what makes these two callable
  // before a session exists (mandatory enrolment). From Settings it is
  // omitted and the api falls back to the session — see
  // PasskeyController::resolveUser().
  registerOptions(enrolmentToken?: string) {
    const body: any = {};
    if (enrolmentToken) {
      body.enrolment_token = enrolmentToken;
    }
    return this.http.post<any>(`${environment.apiUrl}/passkey/register-options`, body);
  }

  registerVerify(credential: any, name: string, enrolmentToken?: string) {
    const body: any = { credential, name };
    if (enrolmentToken) {
      body.enrolment_token = enrolmentToken;
    }
    return this.http.post<any>(`${environment.apiUrl}/passkey/register-verify`, body);
  }

  /**
   * JS-1396 follow-up: the registerOptions -> createCredential ->
   * registerVerify sequence, extracted so it is written once rather than
   * hand-copied at every enrolment call site (passkey-enrol.component.ts,
   * security-tab.component.ts, and now the recovery-code add-device
   * prompt). Returns the register-verify response unchanged.
   *
   * Only createCredential() runs purely on-device and sends no request; a
   * failure there is wrapped in PasskeyLocalCeremonyError so a caller that
   * needs the distinction (passkey-enrol.component.ts, which must NOT bounce
   * the user off the mandatory screen for a local failure) can still make
   * it after the sequence is collapsed into one call. registerOptions and
   * registerVerify failures propagate as whatever the HTTP call rejected
   * with - unchanged from today.
   */
  async enrol(name: string, enrolmentToken?: string): Promise<any> {
    const optionsResponse = await this.registerOptions(enrolmentToken).toPromise();

    let credential: any;
    try {
      credential = await this.createCredential(optionsResponse.options);
    } catch (error) {
      throw new PasskeyLocalCeremonyError(error);
    }

    return this.registerVerify(credential, name, enrolmentToken).toPromise();
  }

  credentials() {
    return this.http.get<any>(`${environment.apiUrl}/passkey/credentials`);
  }

  removeCredential(id: number, password: string) {
    // JS-1396 (Security tab review, round 2): briefly routed through
    // rawHttp + manually-attached auth headers (round 1) so the component
    // could branch on the real HTTP status. Reverted: rawHttp is built on
    // HttpBackend and never runs ErrorInterceptor at all, which silently
    // dropped its 401 (session expired -> logout + reload) and 409
    // agency-switched (stale session -> resync + reload) handling on these
    // two authenticated, security-critical calls - a materially worse
    // failure mode than the status-branching it was meant to enable. Back
    // on the normal, interceptor-wrapped `http`: the interceptor's own
    // `tap` reads the api's literal error message and toasts it translated
    // ("Password is incorrect." on a wrong password, matching the api's
    // exact response), so callers no longer need `error.status` here at
    // all. See loginRecovery() below for the one call where the rawHttp
    // bypass is still correct - it's unauthenticated, so 401/409-agency-
    // switch are structurally impossible there and the bypass costs
    // nothing.
    return this.http.request<any>('delete', `${environment.apiUrl}/passkey/credentials/${id}`, { body: { password } });
  }

  regenerateRecoveryCodes(password: string) {
    return this.http.post<any>(`${environment.apiUrl}/passkey/recovery-codes`, { password });
  }

  loginOptions(loginToken: string) {
    return this.http.post<any>(`${environment.apiUrl}/passkey/login-options`, { login_token: loginToken });
  }

  // JS-1396. Through rawHttp so the caller sees the real status (401 = full
  // login needed, 422 = no passkey); rawHttp skips the interceptors, so the
  // bearer and this device's session are attached by hand.
  unlockOptions() {
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    const token = currentUser && currentUser.api_token;
    return this.rawHttp.post<any>(`${environment.apiUrl}/passkey/unlock-options`, {}, {
      headers: {Authorization: `Bearer ${token}`, ...webSessionHeaders()}
    });
  }

  // JS-1396: revives this device's session; no new token is issued.
  unlockVerify(loginToken: string, credential: any) {
    return this.http.post<any>(`${environment.apiUrl}/passkey/unlock-verify`, {login_token: loginToken, credential});
  }

  loginVerify(loginToken: string, credential: any) {
    return this.http.post<any>(`${environment.apiUrl}/passkey/login-verify`, { login_token: loginToken, credential });
  }

  loginRecovery(loginToken: string, code: string) {
    // See the constructor comment: deliberately bypasses the (duplicated)
    // interceptor chain so callers get a real HttpErrorResponse with a
    // trustworthy `.status`.
    return this.rawHttp.post<any>(`${environment.apiUrl}/passkey/login-recovery`, { login_token: loginToken, code });
  }

  recoveryEmail(loginToken: string) {
    // Same bypass, same reasoning as loginRecovery() above: unauthenticated,
    // so the 401/409-agency-switch handling rawHttp gives up is structurally
    // unreachable here. Needed so the ErrorInterceptor's own toast (fired on
    // a 400 "Unknown origin.") can't appear alongside this screen's single
    // deliberately-identical confirmation message.
    return this.rawHttp.post<any>(`${environment.apiUrl}/passkey/recovery-email`, { login_token: loginToken });
  }

  /**
   * "Set up a passkey on this device": emails a 6-digit code. The first send
   * passes the login_token from the password step; a resend passes the
   * device_token the first send returned, because the login_token's
   * five-minute TTL can run out while the user waits for the email.
   * rawHttp for the same reason as loginRecovery(): the screen branches on
   * the real status (403 dead token vs 429 budget spent vs 429 throttle).
   */
  deviceCode(tokens: { loginToken?: string, deviceToken?: string }) {
    const body: any = {};
    if (tokens.loginToken) {
      body.login_token = tokens.loginToken;
    }
    if (tokens.deviceToken) {
      body.device_token = tokens.deviceToken;
    }
    return this.rawHttp.post<any>(`${environment.apiUrl}/passkey/device-code`, body);
  }

  /** Answers an enrolment_token for enrol() once the emailed code matches. */
  deviceVerify(deviceToken: string, code: string) {
    return this.rawHttp.post<any>(`${environment.apiUrl}/passkey/device-verify`, { device_token: deviceToken, code });
  }

  resetConfirm(token: string) {
    return this.http.post<any>(`${environment.apiUrl}/passkey/reset-confirm`, { token });
  }

  /** Runs the browser registration ceremony and returns a JSON-safe credential. */
  async createCredential(options: any): Promise<any> {
    const publicKey: any = {
      ...options,
      challenge: base64UrlToBuffer(options.challenge),
      user: { ...options.user, id: base64UrlToBuffer(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map((c: any) => ({
        ...c,
        id: base64UrlToBuffer(c.id)
      }))
    };

    const credential: any = await navigator.credentials.create({ publicKey });

    return {
      id: credential.id,
      rawId: bufferToBase64Url(credential.rawId),
      type: credential.type,
      response: {
        clientDataJSON: bufferToBase64Url(credential.response.clientDataJSON),
        attestationObject: bufferToBase64Url(credential.response.attestationObject)
      }
    };
  }

  /**
   * Runs the browser authentication ceremony and returns a JSON-safe assertion.
   * `signal` (optional) lets the caller close the OS sheet, e.g. when the
   * screen is left while it is open.
   */
  async getCredential(options: any, signal?: AbortSignal): Promise<any> {
    const publicKey: any = {
      ...options,
      challenge: base64UrlToBuffer(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((c: any) => ({
        ...c,
        id: base64UrlToBuffer(c.id)
      }))
    };

    const assertion: any = await navigator.credentials.get(signal ? { publicKey, signal } : { publicKey });

    return {
      id: assertion.id,
      rawId: bufferToBase64Url(assertion.rawId),
      type: assertion.type,
      response: {
        clientDataJSON: bufferToBase64Url(assertion.response.clientDataJSON),
        authenticatorData: bufferToBase64Url(assertion.response.authenticatorData),
        signature: bufferToBase64Url(assertion.response.signature),
        userHandle: assertion.response.userHandle ? bufferToBase64Url(assertion.response.userHandle) : null
      }
    };
  }
}
